/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/set-state-in-effect */
import { createContext, useContext, useState, useCallback, useMemo, useEffect, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useAuthValue } from './AuthContext'
import { useBranding } from './UiCustomizationContext'
import { useAppSuiteSettings } from './AppSuiteSettingsContext'
import { legacyRedirectTarget, type LegacyAppMode } from '../utils/appSuites/legacyRedirect'

export type AppMode = LegacyAppMode | 'apps'

interface AppModeContextValue {
  appMode: AppMode
  isMilkyway: boolean
  isPlatform: boolean
  isDataflow: boolean
  isApps: boolean
  experienceResolving: boolean
  switchMode: (mode: LegacyAppMode) => void
  toggleMode: () => void
  enterApps: () => void
  enterClassic: () => void
}

const STORAGE_KEY = 'tagp-app-mode'

const BUSINESS_USER_ROLES = new Set(['business_user', 'business-user', 'businessuser'])

function normalizeRoleName(roleName: string): string {
  return roleName.toLowerCase().replace(/[\s-]/g, '_')
}

function isBusinessUserRole(roleName: string): boolean {
  return BUSINESS_USER_ROLES.has(normalizeRoleName(roleName))
}

/** True when the signed-in user has a business-user role (Milkyway / non-admin persona). */
export function hasBusinessUserRole(roles: { name: string }[] | undefined | null): boolean {
  return Boolean(roles?.some((role) => isBusinessUserRole(role.name)))
}

function detectDefaultMode(
  roles: { name: string }[] | undefined | null,
  platformOnly: boolean,
): LegacyAppMode {
  // Platform-only deployments are locked to the Platform view.
  if (platformOnly) return 'platform'
  if (hasBusinessUserRole(roles)) {
    return 'milkyway'
  }
  return 'platform'
}

function readPersistedMode(): LegacyAppMode | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'milkyway' || stored === 'platform' || stored === 'dataflow') return stored
  } catch { /* ignore */ }
  return null
}

function persistMode(mode: LegacyAppMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode)
  } catch { /* ignore */ }
}

const AppModeContext = createContext<AppModeContextValue | undefined>(undefined)

export function AppModeProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuthValue()
  const { platform_only: platformOnly } = useBranding()
  const { experience, isResolving, setUserChoice } = useAppSuiteSettings()
  const navigate = useNavigate()
  const location = useLocation()

  const [legacyMode, setLegacyMode] = useState<LegacyAppMode>(() => {
    const persisted = readPersistedMode()
    if (persisted) return persisted
    return detectDefaultMode(currentUser?.roles, platformOnly)
  })

  // Re-evaluate default when user first loads (roles may arrive after initial render)
  useEffect(() => {
    if (!currentUser?.roles) return
    const persisted = readPersistedMode()
    if (!persisted) {
      setLegacyMode(detectDefaultMode(currentUser.roles, platformOnly))
    }
  }, [currentUser?.roles, platformOnly])

  // Platform-only deployments: coerce back into the Platform view if the customer
  // config locks it (branding loads after first render, or a stale persisted mode).
  useEffect(() => {
    if (platformOnly && legacyMode !== 'platform') {
      setLegacyMode('platform')
      persistMode('platform')
    }
  }, [platformOnly, legacyMode])

  const applyLegacyRedirect = useCallback(
    (mode: LegacyAppMode) => {
      const target = legacyRedirectTarget(mode, location.pathname)
      if (!target) return
      navigate(target.path, { replace: true, state: target.state })
    },
    [navigate, location.pathname],
  )

  const switchMode = useCallback(
    (mode: LegacyAppMode) => {
      // Platform-only deployments cannot leave the Platform view.
      if (platformOnly && mode !== 'platform') return
      setLegacyMode(mode)
      persistMode(mode)
      applyLegacyRedirect(mode)
    },
    [applyLegacyRedirect, platformOnly],
  )

  const toggleMode = useCallback(() => {
    // Platform-only deployments have no other mode to cycle into.
    if (platformOnly) return
    const modeOrder: LegacyAppMode[] = ['platform', 'milkyway', 'dataflow']
    const currentIdx = modeOrder.indexOf(legacyMode)
    const nextIdx = (currentIdx + 1) % modeOrder.length
    switchMode(modeOrder[nextIdx])
  }, [legacyMode, switchMode, platformOnly])

  const enterApps = useCallback(() => {
    setUserChoice('apps')
    navigate('/')
  }, [navigate, setUserChoice])

  const enterClassic = useCallback(() => {
    setUserChoice('classic')
    applyLegacyRedirect(legacyMode)
  }, [applyLegacyRedirect, legacyMode, setUserChoice])

  const appMode: AppMode = experience === 'apps' ? 'apps' : legacyMode

  const value = useMemo<AppModeContextValue>(
    () => ({
      appMode,
      isMilkyway: appMode === 'milkyway',
      isPlatform: appMode === 'platform',
      isDataflow: appMode === 'dataflow',
      isApps: appMode === 'apps',
      experienceResolving: isResolving,
      switchMode,
      toggleMode,
      enterApps,
      enterClassic,
    }),
    [appMode, isResolving, switchMode, toggleMode, enterApps, enterClassic],
  )

  return <AppModeContext.Provider value={value}>{children}</AppModeContext.Provider>
}

export function useAppMode(): AppModeContextValue {
  const context = useContext(AppModeContext)
  if (context === undefined) {
    throw new Error('useAppMode must be used within an AppModeProvider')
  }
  return context
}
